"use client";

import { Swiper, SwiperSlide } from "swiper/react";

import { LinkElement } from "../link/link";
import { CustomcContentSlide, Slider as SliderType } from "../../../sanity/lib/types/slider";
import useIsMobile from "../../hooks/useIsMobile";
import { ImageElement } from "../Image";
import { VideoElement } from "../Video";
import { LocalizedLink } from "../link/localizedLink";

export default function ({ slider, margin = true }: { slider: SliderType; margin?: boolean }) {
    const { isMobile } = useIsMobile();

    const isTitleVisible =
        slider.title &&
        slider.showTitle &&
        slider.showTitle?.indexOf("desktop") !== -1 &&
        !isMobile;

    return (
        <section title={slider.title || "Slider"}>
            {slider.showTitle?.indexOf("desktop") === -1 &&
                slider.showTitle?.indexOf("mobile") === -1 && (
                    <h2 className="absolute -top-[100vh]">{slider.title}</h2>
                )}
            <div className="my-5 md:my-16">
                {slider.title && (
                    <div className="container">
                        <div className={`${margin ? "mx-2.5 md:mx-5" : ""}`}>
                            {slider.title &&
                                slider.showTitle?.indexOf("mobile") !== -1 &&
                                isMobile && (
                                    <h2 className="relative mb-8 m-title">{slider.title}</h2>
                                )}
                        </div>
                    </div>
                )}
                <div
                    className={`flex flex-col-reverse gap-8 ${margin ? "ml-2.5 md:ml-5" : ""} md:flex-col`}
                >
                    <div
                        className={`container flex items-center ${isTitleVisible ? "justify-between" : "justify-end"}`}
                    >
                        {isTitleVisible && <h2 className="d-title ml-2.5">{slider.title}</h2>}

                        {slider?.link && (
                            <div className="relative z-10 mr-5">
                                <span>
                                    <LinkElement size="small" className="btn" link={slider?.link} />
                                </span>
                            </div>
                        )}
                    </div>

                    <div className="container">
                        <div className="overflow-x-hidden 3xl:ml-2.5">
                            <Swiper
                                slidesPerView={1.2}
                                spaceBetween={20}
                                grabCursor={true}
                                breakpoints={{
                                    687: {
                                        slidesPerView: 1.8,
                                    },
                                    900: {
                                        slidesPerView: 2.8,
                                    },
                                }}
                                role="group"
                                aria-roledescription="carousel"
                                aria-label="Solution Slider"
                            >
                                {slider.type === "solutions" &&
                                    slider.solutions?.map((solution, index) => (
                                        <SwiperSlide
                                            key={index}
                                            className="swiper-slide"
                                            role="group"
                                            aria-roledescription="slide"
                                            aria-label={`Solution ${index + 1}: ${solution.previewTitle ?? "ohne Titel"}`}
                                        >
                                            <LocalizedLink
                                                slug={`solutions/${solution.metadata?.slug.current}`}
                                                className="container flex flex-col gap-4 mb-16"
                                                aria-label={`Go to Solution: ${solution.previewTitle}`}
                                                label={
                                                    <>
                                                        {solution.previewImage?.asset?.[0]._type ===
                                                        "image" ? (
                                                            <div className="overflow-hidden">
                                                                <ImageElement
                                                                    src={
                                                                        solution.previewImage
                                                                            ?.asset[0]
                                                                    }
                                                                    width={673}
                                                                    alt={
                                                                        solution.previewImage?.alt
                                                                            ? solution.previewImage
                                                                                  .alt
                                                                            : `Preview Image for ${solution.previewTitle}`
                                                                    }
                                                                    className="w-full h-auto aspect-[492/615] object-cover hover-image"
                                                                />
                                                            </div>
                                                        ) : (
                                                            <div className="overflow-hidden">
                                                                <div className="hover-image">
                                                                    <VideoElement
                                                                        useIntersection={true}
                                                                        src={
                                                                            solution.previewImage
                                                                                ?.asset?.[0]
                                                                                .assetUrl?.url || ""
                                                                        }
                                                                        aria-label={
                                                                            solution.previewImage
                                                                                ?.alt
                                                                                ? solution
                                                                                      .previewImage
                                                                                      .alt
                                                                                : `Preview Video for ${solution.previewTitle}`
                                                                        }
                                                                        className="w-full h-auto aspect-[492/615] object-cover pointer-events-none"
                                                                    />
                                                                </div>
                                                            </div>
                                                        )}
                                                        <div className="hidden md:block">
                                                            <h3 className="mb-1.5 d-headline-3">
                                                                {solution.previewTitle}
                                                            </h3>
                                                            <p className="d-body-m">
                                                                {solution.industry?.title}
                                                            </p>
                                                        </div>
                                                    </>
                                                }
                                            />
                                        </SwiperSlide>
                                    ))}
                                {slider.type === "custom" &&
                                    slider.customContent?.map((slide, index) => (
                                        <SwiperSlide
                                            key={index}
                                            className="swiper-slide"
                                            role="group"
                                            aria-roledescription="slide"
                                            aria-label={`Slide ${index + 1}`}
                                        >
                                            {slide.link ? (
                                                <LocalizedLink
                                                    slug={`${slide.link?.internal?.metadata.slug.current}`}
                                                    className="container flex flex-col gap-4 mb-16"
                                                    aria-label={`Go to Page: ${slide.title ?? ""}`}
                                                    label={<SwiperContent solution={slide} />}
                                                />
                                            ) : (
                                                <div className="container flex flex-col gap-4 mb-16">
                                                    {<SwiperContent solution={slide} />}
                                                </div>
                                            )}
                                        </SwiperSlide>
                                    ))}
                            </Swiper>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

const SwiperContent = ({ solution }: { solution: CustomcContentSlide }) => {
    return (
        <>
            {solution.image?.asset?.[0]._type === "image" ? (
                <ImageElement
                    src={solution.image?.asset[0]}
                    width={673}
                    alt={solution.image?.alt}
                    className="w-full h-auto aspect-[492/615] object-cover mt-4"
                />
            ) : (
                <VideoElement
                    src={solution.image?.asset?.[0].assetUrl?.url || ""}
                    aria-label={solution.image?.alt || ""}
                    className="w-full h-auto aspect-[492/615] object-cover mt-4"
                />
            )}
            <div className="hidden md:block">
                <h3 className="mb-4 d-headline-3">{solution.title}</h3>
                <p className="d-body-m">{solution.description}</p>
            </div>
        </>
    );
};
